import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { api, unwrap, uploadFile } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast, type AppMutationMeta } from "../mutationMeta";
import type { MovePreview, SaveVariables } from "../types";
import { isMove } from "./catalog";

type S = components["schemas"];

const keys = {
  directory: ["platform", "catalog", "manufacturers"] as const,
  requests: ["platform", "catalog", "requests"] as const,
  categories: ["platform", "catalog", "categories"] as const,
  attributes: ["platform", "catalog", "attributes"] as const,
  products: ["platform", "catalog", "products"] as const,
};

/** The two K-SHOP directories Kasten curates; both share one API shape. */
export type DirectoryKind = "manufacturers" | "suppliers";

export function useDirectory(kind: DirectoryKind, filters: { q?: string; status?: "active" | "archived" | "all"; page?: number }) {
  return useQuery({
    queryKey: [...keys.directory, kind, "list", filters],
    queryFn: () =>
      kind === "manufacturers"
        ? unwrap(api.GET("/api/platform/catalog/manufacturers/", { params: { query: filters } }))
        : unwrap(api.GET("/api/platform/catalog/suppliers/", { params: { query: filters } })),
    placeholderData: keepPreviousData,
  });
}

/** Names companies use outside the directory, with how many companies use each. */
export function useUnlisted(kind: DirectoryKind) {
  return useQuery({
    queryKey: [...keys.directory, kind, "unlisted"],
    queryFn: () =>
      kind === "manufacturers"
        ? unwrap(api.GET("/api/platform/catalog/manufacturers/unlisted/"))
        : unwrap(api.GET("/api/platform/catalog/suppliers/unlisted/")),
  });
}

export function useSaveDirectoryEntry(kind: DirectoryKind) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<S["DirectoryEntryWriteRequest"], S["PatchedDirectoryEntryWriteRequest"]>) => {
      if (kind === "manufacturers") {
        return vars.id !== undefined
          ? unwrap(api.PATCH("/api/platform/catalog/manufacturers/{id}/", { params: { path: { id: vars.id } }, body: vars.body }))
          : unwrap(api.POST("/api/platform/catalog/manufacturers/", { body: vars.body }));
      }
      return vars.id !== undefined
        ? unwrap(api.PATCH("/api/platform/catalog/suppliers/{id}/", { params: { path: { id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/platform/catalog/suppliers/", { body: vars.body }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...keys.directory, kind] }),
  });
}

/** Restore shows the default error toast; pass `noErrorToast` where a ConfirmDialog renders the error (archive). */
export function useArchiveDirectoryEntry(kind: DirectoryKind, meta: AppMutationMeta = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => {
      const params = { path: { id } };
      if (kind === "manufacturers") {
        return archived
          ? unwrap(api.POST("/api/platform/catalog/manufacturers/{id}/archive/", { params }))
          : unwrap(api.POST("/api/platform/catalog/manufacturers/{id}/restore/", { params }));
      }
      return archived
        ? unwrap(api.POST("/api/platform/catalog/suppliers/{id}/archive/", { params }))
        : unwrap(api.POST("/api/platform/catalog/suppliers/{id}/restore/", { params }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...keys.directory, kind] }),
  });
}

export type DirectoryEntry = S["CatalogManufacturer"] | S["CatalogSupplier"];

/** Replace (file) or remove (null) an entry's logo. */
export function useDirectoryLogo(kind: DirectoryKind) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, file }: { id: string; file: File | null }) =>
      uploadFile<DirectoryEntry>(`/api/platform/catalog/${kind}/${id}/logo/`, file, file ? "POST" : "DELETE"),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...keys.directory, kind] }),
  });
}

/** Kasten merges an entry into another, which is kept (ADR-005). */
export function useMergeDirectoryEntry(kind: DirectoryKind) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, intoId }: { id: string; intoId: string }) => {
      const params = { path: { id } };
      const body = { into_id: intoId };
      return kind === "manufacturers"
        ? unwrap(api.POST("/api/platform/catalog/manufacturers/{id}/merge/", { params, body }))
        : unwrap(api.POST("/api/platform/catalog/suppliers/{id}/merge/", { params, body }));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.directory });
      qc.invalidateQueries({ queryKey: keys.requests });
    },
  });
}

// --- requests companies send to the directories (ADR-004) ---------------------------------------
export type RequestKind = "manufacturer" | "supplier" | "branch";
export type RequestFilters = { kind?: RequestKind; status?: "pending" | "resolved" | "all"; q?: string; page?: number };
export const requestKindOf: Record<DirectoryKind, RequestKind> = { manufacturers: "manufacturer", suppliers: "supplier" };

export function useDirectoryRequests(filters: RequestFilters) {
  return useQuery({
    queryKey: [...keys.requests, "list", filters],
    queryFn: () => unwrap(api.GET("/api/platform/catalog/requests/", { params: { query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useDirectoryRequest(id: string | null) {
  return useQuery({
    queryKey: [...keys.requests, "detail", id],
    enabled: id !== null,
    queryFn: () => unwrap(api.GET("/api/platform/catalog/requests/{id}/", { params: { path: { id: id ?? "" } } })),
  });
}

/** Pending requests per directory (navigation badges). */
export function useRequestCounts(enabled = true) {
  return useQuery({
    queryKey: [...keys.requests, "counts"],
    enabled,
    queryFn: () => unwrap(api.GET("/api/platform/catalog/requests/counts/")),
    refetchInterval: 120_000,
  });
}

type Resolution =
  | { action: "add"; id: string; body: S["RequestAddRequest"] | S["RequestBranchAddRequest"] }
  | { action: "merge"; id: string; body: S["RequestMergeRequest"] }
  | { action: "decline"; id: string; body: S["RequestDeclineRequest"] };

/** Resolve a request; the directories change too (a new entry, an alias, linked companies). */
export function useResolveRequest() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (r: Resolution) => {
      const params = { path: { id: r.id } };
      if (r.action === "add") return unwrap(api.POST("/api/platform/catalog/requests/{id}/add/", { params, body: r.body }));
      if (r.action === "merge") return unwrap(api.POST("/api/platform/catalog/requests/{id}/merge/", { params, body: r.body }));
      return unwrap(api.POST("/api/platform/catalog/requests/{id}/decline/", { params, body: r.body }));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.requests });
      qc.invalidateQueries({ queryKey: keys.directory });
      qc.invalidateQueries({ queryKey: ["platform", "catalog", "branches"] });
    },
  });
}

// --- the K-SHOP standard categories (ADR-003) --------------------------------------------------
// One global tree every company uses. Kasten edits it here; partner companies edit the same tree
// through their tenant endpoints. Item counts and move figures here are every company's.

/** Every product type (unpaginated; build the tree from parent_id); `tree`: another tree's key or id, "" for every tree. */
export function useStandardCategories(status: "active" | "all" = "active", tree: string = "product_type") {
  return useQuery({
    queryKey: [...keys.categories, status, tree],
    queryFn: () => unwrap(api.GET("/api/platform/catalog/categories/", { params: { query: { status, ...(tree ? { tree } : {}) } } })),
    placeholderData: keepPreviousData,
  });
}

export function useStandardAttributes() {
  return useQuery({ queryKey: keys.attributes, queryFn: () => unwrap(api.GET("/api/platform/catalog/attributes/")) });
}

/**
 * The standard tree changed: refresh it here and in any company's cached copy (same data, ADR-003).
 * A move also changes items (their category paths, and values that don't belong in the new place).
 */
export function useStandardChanged() {
  const qc = useQueryClient();
  return (which: "categories" | "attributes", { moved = false }: { moved?: boolean } = {}) => {
    qc.invalidateQueries({ queryKey: which === "categories" ? keys.categories : keys.attributes });
    const tenantKeys = new Set([which === "categories" ? "item-categories" : "item-attributes", ...(moved ? ["items"] : [])]);
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "tenant" && tenantKeys.has(String(q.queryKey[2])) });
  };
}

export function useSaveStandardCategory() {
  const changed = useStandardChanged();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<S["ItemCategoryWriteRequest"], S["PatchedItemCategoryWriteRequest"]>) =>
      vars.id !== undefined
        ? unwrap(api.PATCH("/api/platform/catalog/categories/{id}/", { params: { path: { id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/platform/catalog/categories/", { body: vars.body })),
    onSuccess: (_saved, vars) => changed("categories", { moved: isMove(vars) }),
  });
}

/**
 * What moving a standard category under `parentId` (null: top level) would do to every company's item
 * values. Always fetched fresh: the answer decides whether a move needs confirming.
 */
export function useStandardMovePreview() {
  const qc = useQueryClient();
  return useCallback(
    (id: string, parentId: string | null): Promise<MovePreview> =>
      qc.fetchQuery({
        queryKey: [...keys.categories, "move-preview", id, parentId ?? ""],
        queryFn: () =>
          unwrap(api.GET("/api/platform/catalog/categories/{id}/move-preview/", { params: { path: { id }, query: { parent_id: parentId ?? "" } } })),
        staleTime: 0,
        gcTime: 0,
      }),
    [qc],
  );
}

/** Restore shows the default error toast; pass `noErrorToast` where a ConfirmDialog renders the error (archive). */
export function useArchiveStandardCategory(meta: AppMutationMeta = {}) {
  const changed = useStandardChanged();
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => {
      const params = { path: { id } };
      return archived
        ? unwrap(api.POST("/api/platform/catalog/categories/{id}/archive/", { params }))
        : unwrap(api.POST("/api/platform/catalog/categories/{id}/restore/", { params }));
    },
    onSuccess: () => changed("categories"),
  });
}

export function useSaveStandardAttribute() {
  const changed = useStandardChanged();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<S["ItemAttributeWriteRequest"], S["PatchedItemAttributeWriteRequest"]>) =>
      vars.id !== undefined
        ? unwrap(api.PATCH("/api/platform/catalog/attributes/{id}/", { params: { path: { id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/platform/catalog/attributes/", { body: vars.body })),
    onSuccess: () => changed("attributes"),
  });
}

// --- the K-SHOP product catalog (ADR-006) ---------------------------------------------------------
// Products come from companies' items automatically; Kasten reviews the pending ones and corrects any.
// A change reaches every linked item of every company at once.
export type ProductStatus = S["ProductStatusEnum"];
export type ProductFilters = { status?: ProductStatus | "all"; q?: string; page?: number };

export function useProducts(filters: ProductFilters) {
  return useQuery({
    queryKey: [...keys.products, "list", filters],
    queryFn: () => unwrap(api.GET("/api/platform/catalog/products/", { params: { query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: [...keys.products, "detail", id],
    enabled: id !== undefined,
    queryFn: () => unwrap(api.GET("/api/platform/catalog/products/{id}/", { params: { path: { id: id ?? "" } } })),
  });
}

/** Pending products some item uses (navigation badge). */
export function useProductCounts(enabled = true) {
  return useQuery({
    queryKey: [...keys.products, "counts"],
    enabled,
    queryFn: () => unwrap(api.GET("/api/platform/catalog/products/counts/")),
    refetchInterval: 120_000,
  });
}

/** Linked items change with their product: refresh any company's cached items too. */
function useProductsChanged() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: keys.products });
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "tenant" && q.queryKey[2] === "items" });
  };
}

export function useSaveProduct() {
  const changed = useProductsChanged();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, body }: { id: string; body: S["PatchedCatalogProductWriteRequest"] }) =>
      unwrap(api.PATCH("/api/platform/catalog/products/{id}/", { params: { path: { id } }, body })),
    onSuccess: changed,
  });
}

type ProductDecision = { action: "validate"; id: string } | { action: "decline"; id: string; response: string };

export function useDecideProduct() {
  const changed = useProductsChanged();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (d: ProductDecision) => {
      const params = { path: { id: d.id } };
      if (d.action === "validate") return unwrap(api.POST("/api/platform/catalog/products/{id}/validate/", { params }));
      return unwrap(api.POST("/api/platform/catalog/products/{id}/decline/", { params, body: { response: d.response } }));
    },
    onSuccess: changed,
  });
}

/** A duplicate merged into the product kept: its items move there. */
export function useMergeProduct() {
  const changed = useProductsChanged();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, intoId }: { id: string; intoId: string }) =>
      unwrap(api.POST("/api/platform/catalog/products/{id}/merge/", { params: { path: { id } }, body: { into_id: intoId } })),
    onSuccess: changed,
  });
}

// --- supplier branches (ADR-004) -------------------------------------------------------------------
export type PlatformBranch = S["PlatformBranch"];
export type BranchFilters = { supplier?: string; region?: string; q?: string; status?: "active" | "archived" | "all"; page?: number };
const branchKeys = ["platform", "catalog", "branches"] as const;

export function useBranches(filters: BranchFilters, enabled = true) {
  return useQuery({
    queryKey: [...branchKeys, filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/platform/catalog/branches/", { params: { query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useSaveBranch() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<S["PlatformBranchWriteRequest"], S["PatchedPlatformBranchWriteRequest"]>) =>
      vars.id !== undefined
        ? unwrap(api.PATCH("/api/platform/catalog/branches/{id}/", { params: { path: { id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/platform/catalog/branches/", { body: vars.body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: branchKeys }),
  });
}

export function useArchiveBranch(meta: AppMutationMeta = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived
        ? unwrap(api.POST("/api/platform/catalog/branches/{id}/archive/", { params: { path: { id } } }))
        : unwrap(api.POST("/api/platform/catalog/branches/{id}/restore/", { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: branchKeys }),
  });
}
